import React, { useState } from 'react';
import { FileText, Mail, Phone, Bot, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';

export interface InboxOutput {
  type: 'event' | 'task' | 'update';
  id: string;
  title: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  onView?: () => void;
}

export interface InboxItemProps {
  type: 'document' | 'email' | 'call' | 'agent';
  title: string;
  subtitle: string;
  timestamp: string;
  outputs: InboxOutput[];
  onOpenSource?: () => void;
}

const TYPE_ICONS = {
  document: FileText,
  email: Mail,
  call: Phone,
  agent: Bot,
} as const;

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getOutputDotColor(output: InboxOutput): string {
  if (output.type === 'event' && output.severity) {
    return SEVERITY_HEX_COLORS[output.severity] || '#9CA3AF';
  }
  if (output.type === 'task') return '#3B82F6';
  return '#9CA3AF';
}

const InboxItem: React.FC<InboxItemProps> = ({
  type,
  title,
  subtitle,
  timestamp,
  outputs,
  onOpenSource,
}) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = TYPE_ICONS[type];

  return (
    <div className="eg-card overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <div
          className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          <Icon size={16} style={{ color: 'var(--color-text-secondary)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium eg-text-primary truncate">{title}</span>
          </div>
          <p className="text-xs eg-text-muted truncate">{subtitle}</p>
        </div>
        <span className="text-xs eg-text-muted flex-shrink-0">{timeAgo(timestamp)}</span>
        <div className="flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div
          className="px-4 pb-3 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          {outputs.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-medium uppercase tracking-wider eg-text-muted mb-2">
                Created from this:
              </p>
              <div className="space-y-1.5">
                {outputs.map((output) => (
                  <div
                    key={output.id}
                    className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-[var(--color-surface-hover)] transition-colors"
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getOutputDotColor(output) }}
                    />
                    <span className="text-sm eg-text-primary flex-1 truncate">
                      {output.title}
                    </span>
                    {output.onView && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          output.onView?.();
                        }}
                        className="flex items-center gap-1 text-xs font-medium flex-shrink-0 hover:opacity-80 transition-opacity"
                        style={{ color: '#17c480' }}
                      >
                        View <ArrowRight size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {onOpenSource && (
            <div className="mt-3 pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button
                onClick={onOpenSource}
                className="flex items-center gap-1 text-xs font-medium hover:opacity-80 transition-opacity"
                style={{ color: '#17c480' }}
              >
                Open Source <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InboxItem;
